// Adapted from ReactBits (https://reactbits.dev) — MIT License
import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';

export default function TrueFocus({
  sentence = 'True Focus',
  separator = ' ',
  manualMode = false,
  blurAmount = 5,
  borderColor = '#0891b2',
  glowColor = 'rgba(8, 145, 178, 0.6)',
  animationDuration = 0.5,
  pauseBetweenAnimations = 1,
  className = '',
}) {
  const words = sentence.split(separator);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastActiveIndex, setLastActiveIndex] = useState(null);
  const containerRef = useRef(null);
  const wordRefs = useRef([]);
  const [focusRect, setFocusRect] = useState({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    if (!manualMode) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % words.length);
      }, (animationDuration + pauseBetweenAnimations) * 1000);
      return () => clearInterval(interval);
    }
  }, [manualMode, animationDuration, pauseBetweenAnimations, words.length]);

  useEffect(() => {
    if (currentIndex === null || currentIndex === -1) return;
    if (!wordRefs.current[currentIndex] || !containerRef.current) return;
    const parentRect = containerRef.current.getBoundingClientRect();
    const activeRect = wordRefs.current[currentIndex].getBoundingClientRect();
    setFocusRect({
      x: activeRect.left - parentRect.left,
      y: activeRect.top - parentRect.top,
      width: activeRect.width,
      height: activeRect.height,
    });
  }, [currentIndex, words.length]);

  const handleMouseEnter = (index) => {
    if (manualMode) {
      setLastActiveIndex(index);
      setCurrentIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (manualMode) setCurrentIndex(lastActiveIndex);
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        display: 'flex',
        gap: '0.3em',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
      }}
    >
      {words.map((word, index) => {
        const isActive = index === currentIndex;
        return (
          <span
            key={index}
            ref={(el) => (wordRefs.current[index] = el)}
            style={{
              position: 'relative',
              cursor: manualMode ? 'pointer' : 'default',
              filter: isActive ? 'blur(0px)' : `blur(${blurAmount}px)`,
              transition: `filter ${animationDuration}s ease`,
            }}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
          >
            {word}
          </span>
        );
      })}

      {/* Moving corner bracket frame */}
      <motion.div
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', boxSizing: 'content-box' }}
        animate={{
          x: focusRect.x,
          y: focusRect.y,
          width: focusRect.width,
          height: focusRect.height,
          opacity: currentIndex >= 0 ? 1 : 0,
        }}
        transition={{ duration: animationDuration }}
      >
        {/* Four corners */}
        {[
          { top: -8, left: -8, borderTop: `2px solid ${borderColor}`, borderLeft: `2px solid ${borderColor}` },
          { top: -8, right: -8, borderTop: `2px solid ${borderColor}`, borderRight: `2px solid ${borderColor}` },
          { bottom: -8, left: -8, borderBottom: `2px solid ${borderColor}`, borderLeft: `2px solid ${borderColor}` },
          { bottom: -8, right: -8, borderBottom: `2px solid ${borderColor}`, borderRight: `2px solid ${borderColor}` },
        ].map((style, i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              width: '0.6rem',
              height: '0.6rem',
              borderRadius: 2,
              filter: `drop-shadow(0 0 3px ${glowColor})`,
              ...style,
            }}
          />
        ))}
      </motion.div>
    </div>
  );
}
